import { request } from '@/utils/request';

// 服务端API响应接口
export interface ServerApiResponse<T = unknown> {
  data: T;
  code: string;
  success: boolean;
  message: string;
  serverTime: number;
  traceId: string;
}

// 标准API响应类型
export interface ApiResponse<T> {
  data: T;
  pagination?: {
    hasMore: boolean;
    nextPage: number;
    total: number;
  };
  result: {
    status: string;
    message?: string;
    code: string;
  }
}

// 聊天配置类型
export interface ChatConfig {
  workspaceNo?: string;
  appNo: string;
  appName: string;
  appTheme: string;
  description?: string;
  icon?: string;
  onboardingInfo: {
    prologue: string;
    questionFiles?: { question: string; }[];
    // eslint-disable-next-line camelcase
    suggested_questions: string[];
  };
  appVersionNo?: string;
  publishDTO?: {
    id: number;
    tenantId: string;
    workspaceNo: string;
    publishNo: string;
    publishTime: string;
    publishVersionNo: string;
    publishVersionDesc: string;
    publishAppType: string;
    platformConfigInfo: string;
    appNo: string;
    appVersionNo: string;
    sessionTime?: string;
    enableFlag: number;
    gmtModified: string;
    creator: string;
    modifier: string;
    isDeleted: string;
    authLoginInfoDTO: Record<string, unknown>;
    shareLink: string;
  };
}

// 会话记录
export interface SessionData {
  sessionId: string;
  startTime: string;
  userId: string;
  title: string;
  workspaceNo: string;
  appNo: string;
  isLike: 1 | 0 | -1;
}

/**
 * 获取聊天配置
 * 使用request，会自动添加token
 */
export const fetchAppConfig = async ({ url, appNo }: { url: string; appNo: string }): Promise<ApiResponse<ChatConfig>> => {
  const response = await request.get<ApiResponse<ChatConfig>>(`${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-za-application': appNo,
    },
  });
  return response.data;
};

/**
 * 发送消息
 * 使用request，会自动添加token
 */
export const sendMessage = async ({ url, appNo, msg }: { url: string; appNo: string; msg: string }): Promise<ApiResponse<ChatConfig>> => {
  const response = await request.post<ApiResponse<ChatConfig>>(`${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sendMsgSteaming`, {
    msg,
  });
  return response.data;
};

/**
 * 获取会话列表
 */
export const fetchSessionList = async ({ url, appNo, pageSize = 10, pageNum = 1, token }:
  { url: string; appNo: string; pageSize?: number; pageNum?: number; token: string }) => {
  const response = await request.get<ApiResponse<SessionData[]>>(`${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sessions?limit=${pageSize}&page=${pageNum}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export interface SessionDetailAction {
  artifactDelta: Record<string, unknown>;
  requestedAuthConfigs: Record<string, unknown>;
  stateDelta: Record<string, unknown>;
  requestedToolConfirmations: Record<string, unknown>;
}

export interface SessionDetailPartFileData {
  displayName: string;
  fileUri: string;
  mimeType: string;
}

export interface SessionDetailPart {
  // 可能包含文本、文件等不同类型字段
  text?: string;
  fileData?: SessionDetailPartFileData;
  [key: string]: unknown;
}

export interface SessionDetail {
  author: string;
  id: string;
  sessionId: string;
  invocationId?: string;
  actions: SessionDetailAction;
  content: {
    role: string; // e.g. 'user' or 'model'
    parts: SessionDetailPart[];
  };
  timestamp: number;
  [key: string]: unknown;
}

/**
 * 获取会话明细
 */
export const fetchSessionDetail = async ({ url, appNo, sessionId, pageSize = 999, pageNum = 1, token }:
  { url: string; appNo: string; sessionId: string; pageSize?: number; pageNum?: number; token: string }) => {
  const response = await request.get<ApiResponse<SessionDetail[]>>(`${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sessionDetails?sessionId=${sessionId}&page=${pageNum}&limit=${pageSize}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

/**
 * 删除会话
 */
export const closeSession = async ({ url, appNo, sessionId, token }:
  { url: string; appNo: string; sessionId: string; token: string }) => {
  const response = await request.delete<ApiResponse<boolean>>(`${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sessions/${sessionId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {}
  });

  return response.data;
};

/**
 * 修改会话标题
 */
export const updateSessionTitle = async ({ url, appNo, sessionId, title, token }:
  { url: string; appNo: string; sessionId: string; title: string; token: string }) => {
  const response = await request.put<ApiResponse<boolean>>(`${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sessions/${sessionId}/updateSessionTitle`, {
    title,
  }, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const addFeedback = async (params: {
  url: string;
  appNo: string;
  token: string;
  requestId: string;
  isLike: 1 | -1;
  feedbackTags?: string;
  feedbackDescription?: string;
}) => {
  const { url, appNo, token, requestId, ...rest } = params;
  const response = await request.post<ApiResponse<void>>(`${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/invocation/${requestId}/feedback`, rest, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};
